import React from 'react';
import { GraduationCap, Award, Calendar, MapPin, ExternalLink } from 'lucide-react';
import { education, projects } from '../data/mock';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const EducationNew = () => {
  return (
    <section id="education" className="py-20 bg-[#fffcf6] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-[#d9b896]/20 to-transparent blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-gradient-to-br from-[#e0d0b8]/30 to-transparent blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <p className="font-handwriting text-3xl mb-2 gradient-text">Where it all began</p>
          <h2 className="text-4xl md:text-6xl font-bold text-[#2d2a26] mb-4">
            <span className="font-serif">Foundation I'm </span>
            <span className="font-display gradient-text-animated">Built On</span>
          </h2>
          <p className="text-lg text-[#737373] max-w-2xl mx-auto">
            Academic foundation and research contributions
          </p>
        </div>

        {/* Education Cards */}
        <div className="mb-20 space-y-8">
          {education.map((edu, index) => (
            <Card
              key={edu.id}
              data-testid={`education-card-${edu.id}`}
              className="bg-white/85 backdrop-blur-sm border-[#e0d0b8] hover:border-[#8b5a3c]/40 transition-all duration-500 hover:shadow-xl overflow-hidden transform hover:-translate-y-1"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className="p-8 md:p-10">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <GraduationCap className="w-6 h-6 text-[#8b5a3c]" />
                      <h3 className="text-2xl font-bold text-[#2d2a26] font-serif">{edu.degree}</h3>
                    </div>

                    <h4 className="text-lg font-semibold mb-2 font-display gradient-text">{edu.institution}</h4>

                    <div className="flex flex-wrap gap-4 text-sm text-[#737373] mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{edu.period}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{edu.location}</span>
                      </div>
                    </div>

                    <p className="text-[#555555] leading-relaxed mb-4">{edu.details}</p>

                    {edu.achievements.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {edu.achievements.map((achievement, idx) => (
                          <Badge key={idx} className="bg-gradient-to-r from-[#b88865]/15 to-[#6b4226]/15 text-[#6b4226] border border-[#8b5a3c]/25">
                            <Award className="w-3 h-3 mr-1" />
                            {achievement}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Projects & Publications */}
        <div>
          <h3 className="text-3xl md:text-5xl font-bold text-[#2d2a26] mb-8 text-center">
            <span className="font-display gradient-text-animated">Research</span>
            <span className="font-serif"> & Publications</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card key={project.id} data-testid={`project-card-${project.id}`} className="bg-white/85 backdrop-blur-sm border-[#e0d0b8] hover:border-[#8b5a3c]/40 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group">
                <div className="p-6">
                  <Badge className="mb-3 bg-gradient-to-r from-[#b88865]/15 to-[#6b4226]/15 text-[#6b4226] border border-[#8b5a3c]/25">
                    {project.category}
                  </Badge>

                  <h4 className="text-lg font-bold text-[#2d2a26] mb-3 group-hover:text-[#8b5a3c] transition-colors font-serif">
                    {project.title}
                  </h4>

                  <p className="text-sm text-[#666666] mb-3 leading-relaxed">{project.description}</p>

                  {project.publication && (
                    <p className="text-xs text-[#999999] mb-3 italic">{project.publication}</p>
                  )}

                  {project.institution && (
                    <p className="text-xs text-[#999999] mb-3">{project.institution}</p>
                  )}

                  <div className="flex flex-wrap gap-2 mb-3">
                    {project.tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs border-[#e0d0b8] text-[#737373]">
                        {tag}
                      </Badge>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-xs font-mono gradient-text font-bold">{project.year}</p>
                    {project.link && (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`project-link-${project.id}`}
                        className="flex items-center gap-1 text-xs text-[#8b5a3c] hover:text-[#6b4226] transition-colors"
                      >
                        View Paper
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EducationNew;
